import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, CheckCircle, Shield, Clock, AlertTriangle, FileText, Stethoscope, Camera, Car, Ban, ArrowRight, ExternalLink } from 'lucide-react';
import SEOHead from '@/components/SEOHead';

const WhatToDoAfterAccidentPage = () => {
  const faqData = [
    {
      question: "Should I call the police after a car accident in Washington?",
      answer: "Yes. In Washington, you're required to report accidents involving injury, death, or property damage over $1,000. Even for minor accidents, a police report creates valuable documentation for your claim."
    },
    {
      question: "How long do I have to see a doctor after a car accident?",
      answer: "See a doctor as soon as possible—ideally within 24-72 hours. Some injuries like whiplash don't show symptoms immediately. Delaying medical care can hurt both your health and your claim."
    },
    {
      question: "Should I talk to the other driver's insurance company?",
      answer: "Be very careful. You're not required to give a recorded statement to the other driver's insurance. They may use your words against you. I recommend talking to a lawyer first."
    },
    {
      question: "What if the accident wasn't my fault?",
      answer: "Even if you weren't at fault, the insurance company may try to assign partial blame to reduce what they pay. Document everything and don't admit any fault at the scene."
    },
    {
      question: "How long do I have to file a claim after a car accident in Washington?",
      answer: "Washington's statute of limitations for personal injury claims is 3 years. However, evidence deteriorates and memories fade. The sooner you act, the stronger your case."
    }
  ];

  const steps = [
    {
      number: 1,
      title: "Check for Injuries",
      description: "Your safety comes first. Check yourself and passengers for injuries. If anyone is hurt, call 911 immediately. Don't move seriously injured people unless there's immediate danger.",
      icon: Stethoscope,
      important: "Adrenaline can mask pain. Even if you feel fine, get medical attention within 24-72 hours."
    },
    {
      number: 2,
      title: "Call 911",
      description: "In Washington, you must report accidents involving injury or significant property damage. The police report becomes important documentation for your claim.",
      icon: Phone,
      important: "Get the responding officer's name and badge number. Ask how to obtain the report."
    },
    {
      number: 3,
      title: "Document the Scene",
      description: "Use your phone to photograph vehicle damage, the accident scene, skid marks, traffic signs, and any visible injuries. Get contact information from witnesses.",
      icon: Camera,
      important: "Take photos before vehicles are moved if it's safe to do so."
    },
    {
      number: 4,
      title: "Exchange Information",
      description: "Get the other driver's name, phone, insurance company, policy number, and license plate. Share your information too—but nothing more.",
      icon: FileText,
      important: "Don't discuss fault or apologize. Even 'I'm sorry' can be used against you."
    },
    {
      number: 5,
      title: "Do NOT Give a Recorded Statement",
      description: "The other driver's insurance will call, sometimes within hours. They'll ask for a recorded statement. You are not required to provide one, and I strongly recommend you don't.",
      icon: Ban,
      important: "Insurance adjusters are trained to get statements they can use to reduce your claim."
    },
    {
      number: 6,
      title: "Get Medical Care",
      description: "See a doctor even if you feel okay. Injuries like whiplash, concussions, and internal bleeding often don't show symptoms immediately. Your medical records also document your injuries for your claim.",
      icon: Stethoscope,
      important: "Follow your doctor's treatment plan. Gaps in treatment hurt your case."
    },
    {
      number: 7,
      title: "Consider Calling a Lawyer",
      description: "If you have significant injuries, medical bills, or the insurance company is pressuring you, a lawyer can protect your interests and handle the insurance company for you.",
      icon: Shield,
      important: "Most personal injury lawyers offer free consultations and work on contingency—no fee unless you win."
    }
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="what-to-do-after-accident-page">
      <SEOHead 
        title="What To Do After a Car Accident in Washington | Step-by-Step Guide | Amy C. Brown Law"
        description="Step-by-step guide on what to do after a car accident in Washington State. From the scene to dealing with insurance. Free consultation: (425) 675-1224."
        faqData={faqData}
        serviceData={{
          name: "Car Accident Legal Guidance",
          description: "Legal guidance and representation for car accident victims in Washington State. Step-by-step advice on protecting your rights after a crash.",
          category: "Personal Injury Law - Auto Accidents"
        }}
      />

      {/* Hero Section */}
      <section className="bg-white py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
              <FileText className="w-4 h-4" />
              Washington State Guide
            </div>
            
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-secondary mb-6 leading-tight">
              What To Do After a Car Accident in Washington
            </h1>
            
            <p className="text-lg text-slate-600 mb-6 leading-relaxed">
              You've just been in a crash. Your heart is racing. You're not sure what to do next. I've helped countless people through exactly this moment—here's what you need to know.
            </p>

            <div className="bg-accent-soft rounded-xl p-6 mb-8 border-l-4 border-primary">
              <p className="text-slate-700 leading-relaxed">
                The decisions you make in the hours and days after an accident can significantly impact your ability to get fair compensation. This guide covers the critical steps—and the mistakes to avoid.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <a 
                href="tel:425-675-1224"
                className="inline-flex items-center justify-center gap-2 bg-primary text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-primary/90 transition-all shadow-lg hover:shadow-xl"
                data-testid="hero-call-btn"
              >
                <Phone className="w-5 h-5" />
                Call (425) 675-1224
              </a>
              <a 
                href="sms:425-675-1224"
                className="inline-flex items-center justify-center gap-2 border-2 border-secondary text-secondary px-8 py-4 rounded-full font-semibold text-lg hover:bg-secondary hover:text-white transition-all"
                data-testid="hero-text-btn"
              >
                <MessageCircle className="w-5 h-5" />
                Text Me
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Steps Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold text-secondary mb-4">
              7 Steps to Protect Yourself After a Crash
            </h2>
            <p className="text-slate-600">
              Follow these steps to protect your health, your rights, and your potential claim.
            </p>
          </div>

          <div className="space-y-8">
            {steps.map((step) => (
              <div key={step.number} className="bg-slate-50 rounded-2xl p-8 border border-slate-100">
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0">
                    <div className="bg-primary text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl">
                      {step.number}
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <step.icon className="w-6 h-6 text-primary" />
                      <h3 className="font-heading text-xl font-semibold text-secondary">{step.title}</h3>
                    </div>
                    <p className="text-slate-600 mb-4">{step.description}</p>
                    <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-lg">
                      <p className="text-amber-800 text-sm font-medium">{step.important}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Critical Warning Section */}
      <section className="py-16 bg-red-50">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <div className="text-center mb-10">
            <AlertTriangle className="w-12 h-12 text-red-600 mx-auto mb-4" />
            <h2 className="font-heading text-3xl font-bold text-secondary mb-4">
              The Biggest Mistake People Make
            </h2>
          </div>
          
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <h3 className="font-heading text-xl font-semibold text-secondary mb-4">
              Giving a Recorded Statement to the Insurance Company
            </h3>
            <p className="text-slate-600 mb-6">
              Within hours of your accident, the other driver's insurance company will likely call. They'll sound friendly and helpful. They'll ask for a "recorded statement" about what happened.
            </p>
            <p className="text-slate-600 mb-6">
              <strong>Here's what I learned from years of defending insurance companies:</strong> That recorded statement isn't to help you. It's to find ways to reduce or deny your claim. Adjusters are trained to ask questions that can be used against you later.
            </p>
            <div className="bg-slate-50 p-6 rounded-xl">
              <p className="text-slate-700 font-medium mb-2">You are NOT required to give a recorded statement.</p>
              <p className="text-slate-600">You can politely decline and say you'll provide a written statement later. Or you can tell them to contact your attorney.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Personal Note Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">
          <div className="bg-accent-soft rounded-2xl p-8 border-l-4 border-primary">
            <h2 className="font-heading text-2xl font-bold text-secondary mb-4">
              A Note From Amy
            </h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              I've been a lawyer for over ten years, and I've guided countless people through the chaos that follows a car accident. I understand the stress you're feeling right now.
            </p>
            <p className="text-slate-700 leading-relaxed mb-4">
              I also understand it personally. Multiple members of my family were killed in a preventable incident. I know the pain and confusion that comes with trauma.
            </p>
            <p className="text-slate-700 leading-relaxed">
              If you're not sure what to do, call or text me. I'll listen, explain your options clearly, and give you honest advice—even if that means you don't need a lawyer.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold text-secondary mb-4">
              Common Questions After a Washington Car Accident
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {faqData.map((faq, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-sm">
                <h3 className="font-heading font-semibold text-secondary mb-3">{faq.question}</h3>
                <p className="text-slate-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Legal Concepts Section - Entity SEO */}
      <section className="py-12 bg-white border-t border-slate-100">
        <div className="container mx-auto px-4 md:px-6 max-w-5xl">
          <h2 className="font-heading text-xl font-semibold text-secondary mb-6 text-center">
            Understanding Car Accident Law
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <a href="https://en.wikipedia.org/wiki/Traffic_collision" target="_blank" rel="noopener noreferrer" className="bg-slate-50 p-4 rounded-lg hover:bg-primary/5 transition-colors text-center group">
              <span className="text-sm text-slate-700 group-hover:text-primary font-medium inline-flex items-center gap-1">
                Traffic Collision <ExternalLink className="w-3 h-3" />
              </span>
            </a>
            <a href="https://en.wikipedia.org/wiki/Personal_injury" target="_blank" rel="noopener noreferrer" className="bg-slate-50 p-4 rounded-lg hover:bg-primary/5 transition-colors text-center group">
              <span className="text-sm text-slate-700 group-hover:text-primary font-medium inline-flex items-center gap-1">
                Personal Injury <ExternalLink className="w-3 h-3" />
              </span>
            </a>
            <a href="https://en.wikipedia.org/wiki/Statute_of_limitations" target="_blank" rel="noopener noreferrer" className="bg-slate-50 p-4 rounded-lg hover:bg-primary/5 transition-colors text-center group">
              <span className="text-sm text-slate-700 group-hover:text-primary font-medium inline-flex items-center gap-1">
                Statute of Limitations <ExternalLink className="w-3 h-3" />
              </span>
            </a>
            <a href="https://en.wikipedia.org/wiki/Negligence" target="_blank" rel="noopener noreferrer" className="bg-slate-50 p-4 rounded-lg hover:bg-primary/5 transition-colors text-center group">
              <span className="text-sm text-slate-700 group-hover:text-primary font-medium inline-flex items-center gap-1">
                Negligence <ExternalLink className="w-3 h-3" />
              </span>
            </a>
          </div>
          <p className="text-xs text-slate-400 text-center mt-4">
            Learn more about these legal concepts on Wikipedia
          </p>
        </div>
      </section>

      {/* Related Car Accident Resources */}
      <section className="py-12 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <h2 className="font-heading text-xl font-semibold text-secondary mb-6 text-center">
            Related Car Accident Resources
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <Link to="/car-crashes" className="flex items-center gap-3 bg-white p-4 rounded-lg border border-slate-200 hover:border-primary/30 hover:shadow-md transition-all group">
              <Car className="w-6 h-6 text-primary" />
              <div>
                <p className="font-medium text-secondary group-hover:text-primary transition-colors">Car Accident Claims</p>
                <p className="text-sm text-slate-500">Learn about the car accident claim process</p>
              </div>
            </Link>
            <Link to="/should-i-talk-to-insurance" className="flex items-center gap-3 bg-white p-4 rounded-lg border border-slate-200 hover:border-primary/30 hover:shadow-md transition-all group">
              <Shield className="w-6 h-6 text-primary" />
              <div>
                <p className="font-medium text-secondary group-hover:text-primary transition-colors">Talking to Insurance</p>
                <p className="text-sm text-slate-500">What to say—and what not to say</p>
              </div>
            </Link>
            <Link to="/how-much-is-my-case-worth" className="flex items-center gap-3 bg-white p-4 rounded-lg border border-slate-200 hover:border-primary/30 hover:shadow-md transition-all group">
              <FileText className="w-6 h-6 text-primary" />
              <div>
                <p className="font-medium text-secondary group-hover:text-primary transition-colors">Case Value Factors</p>
                <p className="text-sm text-slate-500">What determines your claim's worth</p>
              </div>
            </Link>
            <Link to="/everett-car-accident-lawyer" className="flex items-center gap-3 bg-white p-4 rounded-lg border border-slate-200 hover:border-primary/30 hover:shadow-md transition-all group">
              <ArrowRight className="w-6 h-6 text-primary" />
              <div>
                <p className="font-medium text-secondary group-hover:text-primary transition-colors">Everett Car Accident Lawyer</p>
                <p className="text-sm text-slate-500">Local representation in Snohomish County</p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-secondary">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <h2 className="font-heading text-3xl font-bold text-white mb-6">
            Not Sure What To Do Next?
          </h2>
          <p className="text-slate-300 text-lg mb-8">
            Call or text me. I'll give you honest guidance about your situation—no obligation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:425-675-1224"
              className="inline-flex items-center justify-center gap-2 bg-primary text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-primary/90 transition-all"
            >
              <Phone className="w-5 h-5" />
              Call (425) 675-1224
            </a>
            <a 
              href="sms:425-675-1224"
              className="inline-flex items-center justify-center gap-2 border-2 border-white text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white/10 transition-all"
            >
              <MessageCircle className="w-5 h-5" />
              Text Me
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhatToDoAfterAccidentPage;
